import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getStateLabel, getStateBadgeVariant } from '@/lib/orders/transitions';
import { useParallaxRef } from '@/utils/parallax';

type OrderState = string;

interface Order {
  id: string;
  domains: string[];
  total_usd: number;
  status: OrderState;
  created_at: string;
  escrow_reference?: string | null;
}

interface PurchaseHistoryTabProps {
  orders: Order[];
  ordersLoading: boolean;
}

export default function PurchaseHistoryTab({ orders, ordersLoading }: PurchaseHistoryTabProps) {
  const navigate = useNavigate();
  const subtitleRef = useParallaxRef({ speed: -0.10, axis: 'y', maxTranslate: 32 });

  return (
    <>
      <CardHeader className="text-center pb-12">
        <CardTitle className="text-4xl md:text-5xl font-bold mb-6">Purchase History</CardTitle>
        <CardDescription ref={subtitleRef as any} className="text-xl md:text-2xl text-foreground/70 mb-20">
          Your domain purchases and their status
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {ordersLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No purchase history yet</p>
            <Button onClick={() => navigate('/domains')}>Browse Domains</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="font-semibold">{order.domains.join(', ')}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${order.total_usd.toLocaleString()}</p>
                    <Badge variant={getStateBadgeVariant(order.status)}>
                      {getStateLabel(order.status)}
                    </Badge>
                  </div>
                </div>
                {order.escrow_reference && (
                  <p className="text-xs text-muted-foreground">
                    Ref: {order.escrow_reference.slice(-8)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </>
  );
}

